import React, { useRef, useState } from "react";
import { Field, FieldArray } from "redux-form";
import { IconButton } from "@mui/material";

import { customInput } from "../../../../../common/custom-input";
import styles from "../../../SwmsMastersStyle.module.css";
import layoutStyles from "../../EmergencyPlansLayout.module.css";

const getDefaultPostRescueItem = () => ({
  action: "",
  owner: "",
  timing: ""
});

function PostRescueRows({ fields, loading, dragHandleOnly }) {
  const dragIndexRef = useRef(null);
  const [draggingIndex, setDraggingIndex] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const clearDragState = () => {
    dragIndexRef.current = null;
    setDraggingIndex(null);
    setHoveredIndex(null);
  };

  return (
    <div>
      {fields.length === 0 ? (
        <div className={layoutStyles.RolesEmptyState}>No actions added yet.</div>
      ) : null}

      {fields.length > 0 ? (
        <div className={layoutStyles.RolesRowsContainer}>
          {fields.map((member, index) => (
            <div
              key={member}
              className={layoutStyles.RolesRow}
              draggable={!loading && !dragHandleOnly}
              onDragStart={(event) => {
                if (loading) {
                  event.preventDefault();
                  return;
                }
                if (dragHandleOnly) {
                  return;
                }
                dragIndexRef.current = index;
                setDraggingIndex(index);
                setHoveredIndex(null);
                if (event.dataTransfer) {
                  event.dataTransfer.effectAllowed = "move";
                }
              }}
              onDragEnter={(event) => {
                if (loading || draggingIndex === null || draggingIndex === index) {
                  return;
                }
                event.preventDefault();
                setHoveredIndex(index);
              }}
              onDragOver={(event) => {
                if (loading || dragIndexRef.current === null || draggingIndex === index) {
                  return;
                }
                event.preventDefault();
                setHoveredIndex(index);
                if (event.dataTransfer) {
                  event.dataTransfer.dropEffect = "move";
                }
              }}
              onDragLeave={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) {
                  setHoveredIndex((current) => (current === index ? null : current));
                }
              }}
              onDrop={(event) => {
                if (loading) {
                  return;
                }
                event.preventDefault();
                const fromIndex = dragIndexRef.current;
                if (fromIndex !== null && fromIndex !== index) {
                  fields.move(fromIndex, index);
                }
                clearDragState();
              }}
              onDragEnd={clearDragState}
              style={{
                opacity: draggingIndex === index ? 0.55 : 1,
                borderTop: hoveredIndex === index && draggingIndex !== index ? "2px solid #7C3AED" : "2px solid transparent"
              }}
            >
              <span
                className={layoutStyles.RolesDragHandle}
                draggable={dragHandleOnly && !loading}
                onDragStart={(event) => {
                  if (!dragHandleOnly || loading) {
                    return;
                  }
                  event.stopPropagation();
                  dragIndexRef.current = index;
                  setDraggingIndex(index);
                  setHoveredIndex(null);
                  if (event.dataTransfer) {
                    event.dataTransfer.effectAllowed = "move";
                    event.dataTransfer.setData("text/plain", String(index));
                  }
                }}
                onDragEnd={dragHandleOnly ? clearDragState : undefined}
                style={{ cursor: loading ? "default" : (draggingIndex === index ? "grabbing" : "grab") }}
                title="Drag to reorder action"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512" width="12" height="12">
                    <path fill="currentColor" d="M40 352l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40zm192 0l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40zM40 320c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0zM232 192l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40zM40 160c-22.1 0-40-17.9-40-40L0 72C0 49.9 17.9 32 40 32l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0zM232 32l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40z" />
                </svg>
              </span>
              <span className={layoutStyles.PostRescueIndexChip}>{index + 1}</span>
              <div className={layoutStyles.PostRescueInputColumn}>
                <Field name={`${member}.action`} component={customInput} className={styles.TextField} disabled={loading} placeholder="Action" autoComplete="off" />
              </div>
              <div className={layoutStyles.PostRescueInputColumn}>
                <Field name={`${member}.owner`} component={customInput} className={styles.TextField} disabled={loading} placeholder="Owner" autoComplete="off" />
              </div>
              <div className={layoutStyles.PostRescueInputColumn}>
                <Field name={`${member}.timing`} component={customInput} className={styles.TextField} disabled={loading} placeholder="Timing" autoComplete="off" />
              </div>
              <IconButton
                className={`p-0 ${layoutStyles.IconButtonAutoHeight}`}
                onClick={() => fields.remove(index)}
                disabled={loading}
                title="Delete action"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" className={`${styles.DeleteIcon} ${layoutStyles.MediaDeleteIcon}`}>
                  <path fill="currentColor" d="M136.7 5.9L128 32 32 32C14.3 32 0 46.3 0 64S14.3 96 32 96l384 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-96 0-8.7-26.1C306.9-7.2 294.7-16 280.9-16L167.1-16c-13.8 0-26 8.8-30.4 21.9zM416 144L32 144 53.1 467.1C54.7 492.4 75.7 512 101 512L347 512c25.3 0 46.3-19.6 47.9-44.9L416 144z" />
                </svg>
              </IconButton>
            </div>
          ))}
        </div>
      ) : null}

      <button type="button" className={`${layoutStyles.AddSectionButton} ${layoutStyles.RolesAddButton}`} onClick={() => fields.push(getDefaultPostRescueItem())} disabled={loading}>
        <svg height="12px" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
          <path fill="currentColor" d="M256 80C256 62.3 241.7 48 224 48C206.3 48 192 62.3 192 80L192 192L80 192C62.3 192 48 206.3 48 224C48 241.7 62.3 256 80 256L192 256L192 368C192 385.7 206.3 400 224 400C241.7 400 256 385.7 256 368L256 256L368 256C385.7 256 400 241.7 400 224C400 206.3 385.7 192 368 192L256 192L256 80z" />
        </svg>
        Add Action
      </button>
    </div>
  );
}

export default function PostRescueNotificationSection({ loading, dragHandleOnly = false }) {
  return (
    <div className="container-fluid pb-2">
      <div className="row pt-2">
        <div className="col-12">
          <div className={layoutStyles.RolesColumnHeadings}>
            <span />
            <span className={layoutStyles.PostRescueIndexHeading} />
            <span>Action</span>
            <span>Owner</span>
            <span>Timing</span>
            <span />
          </div>
          <FieldArray name="items" component={PostRescueRows} loading={loading} dragHandleOnly={dragHandleOnly} />
        </div>
      </div>
    </div>
  );
}